import React, { useState } from 'react';

/**
 * 🎯 HOOK PARA EXPORTAÇÃO DE RELATÓRIOS DO BACKEND
 * Este hook faz requisições para o backend e baixa os arquivos gerados
 */
export const useRelatorioExport = () => {
  const [exportando, setExportando] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const [erro, setErro] = useState(null);

  /**
   * 📊 FUNÇÃO PRINCIPAL PARA EXPORTAR RELATÓRIOS
   */
  const exportarRelatorio = async (endpoint, filtros = {}, formato = 'pdf', titulo = 'relatorio') => {
    try {
      setExportando(true);
      setErro(null);
      setProgresso(10);

      console.log('📡 Iniciando exportação:', { endpoint, formato, filtros });

      // Construir URL correta
      const url = buildExportUrl(endpoint, filtros, formato);
      console.log('🌐 URL final:', url);

      setProgresso(30);

      // Fazer requisição para o backend
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : '',
          'Accept': '*/*'
        }
      });

      setProgresso(60);

      console.log('📋 Response Status:', response.status);
      console.log('📋 Response Headers:', Object.fromEntries(response.headers.entries()));

      // Verificar se a resposta é OK
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erro HTTP:', response.status, errorText);
        throw new Error(`Erro ${response.status}: ${errorText.substring(0, 200)}`);
      }

      // Verificar Content-Type
      const contentType = response.headers.get('content-type') || '';
      console.log('📋 Content-Type:', contentType);

      setProgresso(80);

      // Se recebeu HTML em vez de arquivo, é erro
      if (contentType.includes('text/html')) {
        const htmlContent = await response.text();
        console.error('❌ Recebeu HTML em vez de arquivo:', htmlContent.substring(0, 300));
        throw new Error('Resposta inesperada do servidor. A rota de export pode não existir.');
      }

      // Se recebeu JSON, pode ser dados ou erro
      if (contentType.includes('application/json')) {
        const jsonData = await response.json();
        console.log('📊 Recebeu JSON:', jsonData);

        // Se tem erro no JSON
        if (jsonData.error || !jsonData.success) {
          // Se é "em desenvolvimento", baixar os dados como JSON
          if (jsonData.error === 'Exportação em desenvolvimento' && jsonData.data) {
            console.log('📝 Baixando dados JSON (desenvolvimento)');
            await downloadJsonData(jsonData.data, titulo);
            setProgresso(100);
            return { success: true, tipo: 'json', message: 'Dados exportados como JSON' };
          }
          
          throw new Error(jsonData.message || jsonData.error || 'Erro desconhecido');
        }

        // Se success = true mas não é arquivo, baixar dados
        if (jsonData.success && jsonData.data) {
          await downloadJsonData(jsonData.data, titulo);
          setProgresso(100);
          return { success: true, tipo: 'json' };
        }
      }

      // Se chegou aqui, deve ser um arquivo (PDF, Excel, etc.)
      const blob = await response.blob();
      console.log('📄 Blob recebido:', blob.size, 'bytes');

      setProgresso(90);

      // Baixar arquivo
      await downloadFile(blob, formato, titulo, contentType);

      setProgresso(100);
      console.log('✅ Exportação concluída com sucesso!');

      return { success: true, tipo: formato };

    } catch (error) {
      console.error('❌ Erro na exportação:', error);
      setErro(error.message);
      setProgresso(0);
      throw error;
    } finally {
      setExportando(false);
      // Reset progresso após 2 segundos
      setTimeout(() => setProgresso(0), 2000);
    }
  };

  /**
   * 🔧 CONSTRUIR URL DE EXPORT - CORREÇÃO DEFINITIVA BASEADA NO BACKEND REAL
   */
  const buildExportUrl = (endpoint, filtros, formato) => {
    console.log('🔧 buildExportUrl - endpoint recebido:', endpoint);
    
    // MAPEAMENTO CORRETO baseado no backend real
    const endpointMap = {
      '/relatorios-tcc/graduados': '/relatorios-tcc/graduados/export',
      '/relatorios-tcc/produtividade': '/relatorios-tcc/produtividade/export',
      '/relatorios-tcc/atrasos': '/relatorios-tcc/atrasos/export',
      '/relatorios-tcc/plagio-ia': '/relatorios-tcc/plagio-ia/export'
    };
    
    // Usar mapeamento direto
    let finalEndpoint = endpointMap[endpoint];
    
    if (!finalEndpoint) {
      // Fallback: construir manualmente se não estiver no mapeamento
      let clean = endpoint;
      // Remover /api duplicado se existir
      if (clean.startsWith('/api/')) clean = clean.substring(4);
      if (clean.startsWith('api/')) clean = clean.substring(4);
      if (!clean.startsWith('/')) clean = '/' + clean;
      if (!clean.includes('/export')) {
        clean = clean.endsWith('/') ? `${clean}export` : `${clean}/export`;
      }
      finalEndpoint = clean; // Não adicionar /api aqui
    }
    
    console.log('🔧 buildExportUrl - endpoint final:', finalEndpoint);

    // Filtrar parâmetros null/undefined  
    const filtrosLimpos = Object.entries(filtros)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    // Adicionar formato
    const params = new URLSearchParams({
      ...filtrosLimpos,
      formato
    });
    
    console.log('🔧 buildExportUrl - params:', params.toString());

    // URL completa - garantir que não haja /api duplicado
    const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, ''); // Remove barra final se existir
    const fullUrl = `${apiUrl}${finalEndpoint}?${params.toString()}`;
    
    console.log('🔧 buildExportUrl - URL final:', fullUrl);

    return fullUrl;
  };

  /**
   * 📥 BAIXAR ARQUIVO BLOB
   */
  const downloadFile = async (blob, formato, titulo, contentType) => {
    // Determinar extensão baseado no formato ou content-type
    let extensao = 'pdf';
    if (formato === 'excel' || formato === 'xlsx' || contentType.includes('spreadsheet')) {
      extensao = 'xlsx';
    } else if (formato === 'word' || formato === 'docx' || contentType.includes('wordprocessing')) {
      extensao = 'docx';
    } else if (formato === 'json') {
      extensao = 'json';
    }

    // Nome do arquivo
    const nomeArquivo = `${titulo}_${new Date().toISOString().split('T')[0]}.${extensao}`;

    // Criar link de download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeArquivo;
    
    // Adicionar ao DOM, clicar e remover
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Limpar URL
    window.URL.revokeObjectURL(url);

    console.log(`📥 Arquivo baixado: ${nomeArquivo}`);
  };

  /**
   * 📄 BAIXAR DADOS JSON
   */
  const downloadJsonData = async (data, titulo) => {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    await downloadFile(blob, 'json', titulo, 'application/json');
  };

  /**
   * 🧹 LIMPAR ERRO
   */
  const limparErro = () => {
    setErro(null);
  };

  return {
    exportarRelatorio,
    exportando,
    progresso,
    erro,
    limparErro
  };
};

/**
 * 🎯 FUNÇÕES UTILITÁRIAS PARA EXPORTAÇÃO ESPECÍFICA
 */

// Hook específico para relatórios TCC
export const useExportTCC = () => {
  const { exportarRelatorio, exportando, progresso, erro, limparErro } = useRelatorioExport();

  const exportarGraduados = async (filtros = {}, formato = 'pdf') => {
    return await exportarRelatorio('/relatorios-tcc/graduados', filtros, formato, 'graduados');
  };

  const exportarProdutividade = async (filtros = {}, formato = 'pdf') => {
    return await exportarRelatorio('/relatorios-tcc/produtividade', filtros, formato, 'produtividade');
  };

  const exportarAtrasos = async (filtros = {}, formato = 'pdf') => {
    return await exportarRelatorio('/relatorios-tcc/atrasos', filtros, formato, 'atrasos');
  };

  const exportarPlagioIA = async (filtros = {}, formato = 'pdf') => {
    return await exportarRelatorio('/relatorios-tcc/plagio-ia', filtros, formato, 'plagio-ia');
  };

  return {
    exportarGraduados,
    exportarProdutividade,
    exportarAtrasos,
    exportarPlagioIA,
    exportando,
    progresso,
    erro,
    limparErro
  };
};

/**
 * 🚀 FUNÇÃO DIRETA PARA USO SIMPLES
 */
export const exportarRelatorioDireto = async (endpoint, filtros = {}, formato = 'pdf', titulo = 'relatorio') => {
  try {
    console.log('📊 Exportação direta:', { endpoint, formato, filtros });

    // Construir URL
    const buildUrl = (ep, filt, fmt) => {
      let base = ep.startsWith('/api') ? ep.substring(4) : ep; // Remove /api duplicado
      if (!base.startsWith('/')) base = '/' + base;
      if (!base.includes('/export')) {
        base = base.endsWith('/') ? `${base}export` : `${base}/export`;
      }
      const params = new URLSearchParams({ ...filt, formato: fmt });
      const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');
      return `${apiUrl}${base}?${params.toString()}`;
    };

    const url = buildUrl(endpoint, filtros, formato);
    console.log('🌐 URL:', url);

    // Fazer requisição
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : '',
        'Accept': '*/*'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erro ${response.status}: ${errorText.substring(0, 200)}`);
    }

    const contentType = response.headers.get('content-type') || '';

    // Se HTML, é erro
    if (contentType.includes('text/html')) {
      throw new Error('Resposta inesperada do servidor');
    }

    // Se JSON, processar
    if (contentType.includes('application/json')) {
      const jsonData = await response.json();
      if (jsonData.error === 'Exportação em desenvolvimento' && jsonData.data) {
        // Baixar como JSON
        const jsonString = JSON.stringify(jsonData.data, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${titulo}_dados.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return { success: true, tipo: 'json' };
      }
      if (jsonData.error) {
        throw new Error(jsonData.message || jsonData.error);
      }
    }

    // Baixar arquivo
    const blob = await response.blob();
    let extensao = 'pdf';
    if (formato === 'excel' || formato === 'xlsx') extensao = 'xlsx';
    if (formato === 'word' || formato === 'docx') extensao = 'docx';

    const nomeArquivo = `${titulo}_${new Date().toISOString().split('T')[0]}.${extensao}`;
    const url2 = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url2;
    link.download = nomeArquivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url2);

    return { success: true, tipo: formato };

  } catch (error) {
    console.error('❌ Erro na exportação direta:', error);
    throw error;
  }
};

/**
 * 🎨 COMPONENTE DE EXEMPLO PARA TESTE
 */
export const TesteExportacao = () => {
  const { exportarRelatorio, exportando, progresso, erro, limparErro } = useRelatorioExport();

  const testarExport = async (formato) => {
    try {
      await exportarRelatorio(
        '/relatorios-tcc/graduados',
        { periodo: '12', ano: '2025' },
        formato,
        'teste_graduados'
      );
      alert(`✅ Exportação ${formato} concluída!`);
    } catch (error) {
      alert(`❌ Erro: ${error.message}`);
    }
  };

  return (
    <div style={{ padding: '20px', border: '1px solid #ddd', borderRadius: '8px', margin: '20px' }}>
      <h3>🧪 Teste de Exportação</h3>
      
      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={() => testarExport('pdf')} 
          disabled={exportando}
          style={{ margin: '5px', padding: '10px' }}
        >
          📄 Testar PDF
        </button>
        <button 
          onClick={() => testarExport('excel')} 
          disabled={exportando}
          style={{ margin: '5px', padding: '10px' }}
        >
          📊 Testar Excel
        </button>
        <button 
          onClick={() => testarExport('json')} 
          disabled={exportando}
          style={{ margin: '5px', padding: '10px' }}
        >
          📋 Testar JSON
        </button>
      </div>

      {exportando && (
        <div>
          <div>Exportando... {progresso}%</div>
          <div style={{ 
            width: '100%', 
            background: '#f0f0f0', 
            borderRadius: '10px',
            marginTop: '10px'
          }}>
            <div style={{ 
              width: `${progresso}%`, 
              background: '#007bff', 
              height: '20px', 
              borderRadius: '10px',
              transition: 'width 0.3s'
            }}></div>
          </div>
        </div>
      )}

      {erro && (
        <div style={{ 
          color: 'red', 
          background: '#ffe6e6', 
          padding: '10px', 
          borderRadius: '5px',
          marginTop: '10px'
        }}>
          ❌ Erro: {erro}
          <button onClick={limparErro} style={{ marginLeft: '10px' }}>✖</button>
        </div>
      )}
    </div>
  );
};

// 📋 INSTRUÇÕES DE USO:
/*
1. SUBSTITUIR IMPORT NO DashboardTCC.jsx:
   DE: import { useRelatorioExport } from '../services/relatorioExportService';
   PARA: import { useRelatorioExport } from '../services/backendExportService';

2. USO NO COMPONENTE:
   const { exportarRelatorio, exportando, progresso, erro, limparErro } = useRelatorioExport();
   
   // Chamar exportação
   await exportarRelatorio('/relatorios-tcc/graduados', filtros, 'pdf', 'graduados');

3. USO DIRETO:
   import { exportarRelatorioDireto } from '../services/backendExportService';
   await exportarRelatorioDireto('/relatorios-tcc/graduados', filtros, 'pdf', 'graduados');

4. PARA TCC ESPECÍFICO:
   import { useExportTCC } from '../services/backendExportService';
   const { exportarGraduados, exportando } = useExportTCC();
   await exportarGraduados(filtros, 'pdf');
*/